import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ExternalLink, MarketingLink } from "@/components/OptionalLink";
import { formatMarkdown } from "@/components/formatMarkdown";
import { Badge } from "@/components/ui/badge";
import useSWR from "swr";
import { Template } from "@/types/template";
import { cn } from "@/lib/utils";

export const Templates = ({ landing = false }: { landing?: boolean }) => {
  const { data: templates } = useSWR<Template[]>("/api/template?sort=popular&limit=3");

  return (
    <section id="templates" className={cn(landing ? "container" : "col-span-full")}>
      {landing && (
        <div className="text-center">
          <h2 className="font-serif text-[clamp(36px,5.5vw,56px)] leading-[1.05] tracking-tight font-normal mb-4 text-foreground text-balance">
            Run <em className="text-primary">open-source apps</em>
          </h2>
          <p className="md:w-3/4 mx-auto mt-4 mb-8 text-xl text-muted-foreground">
            Launch popular applications with one click.
          </p>
        </div>
      )}

      <Card
        className={cn(
          "bg-gradient-to-br from-slate-950 to-indigo-950 text-white",
          landing && "border"
        )}
      >
        <CardHeader>
          <CardTitle className="text-xl mt-2">Self-host open-source apps</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="flex flex-col gap-4">
              <div className="text-muted-foreground">
                Deploy popular applications with one click. All templates are pre-configured
                and ready to go. Missing a template? Let us know via our{" "}
                <ExternalLink className="link" href="/discord">
                  Discord
                </ExternalLink>
                .
              </div>

              <div>
                <Button variant="outline" asChild>
                  <MarketingLink
                    href="/r"
                    className="plausible-event-name=ViewAllTemplates hover:bg-accent/10 text-foreground hover:text-primary-foreground"
                  >
                    View all
                  </MarketingLink>
                </Button>
              </div>
            </div>

            <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
              {templates?.slice(0, 3).map(template => (
                <MarketingLink
                  key={template.id}
                  className="hover:cursor-pointer bg-background relative hover:scale-110 hover:border-primary/50 transition-all duration-300 hover:bg-muted flex flex-col gap-2 max-h-auto border rounded-lg p-4"
                  href={`/r/${template.id}`}
                >
                  <div className="h-12">
                    {template.logo && (
                      <img
                        src={template.logo}
                        alt={`${template.name} logo`}
                        className="h-12 w-12 object-contain"
                      />
                    )}
                  </div>

                  <div className="text-xl font-bold line-clamp-1 text-foreground">
                    {template.name}
                  </div>

                  <div className="absolute top-4 right-4 flex flex-row justify-center">
                    <Badge variant="secondary">Popular</Badge>
                  </div>

                  <div className="flex flex-row justify-start">
                    <span className="text-sm text-foreground line-clamp-2">
                      {formatMarkdown(template.intro)}
                    </span>
                  </div>
                </MarketingLink>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </section>
  );
};
